import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import router from 'next/router';

import { ArrowButton } from 'shared/components/Carousel/ArrowButton';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { Button } from 'shared/components/Button';
import { GalleryItemVariant } from 'productTemplate/components/buyBar/GalleryItemVariant';
import { IUserProductIdOfferResponse } from 'productTemplate/types/UserProductIdOfferResponse';
import { IVariantDetailProps } from 'productTemplate/components/buyBar/VariantDetail';
import { localizeCondition } from 'shared/utils/localizeCondition';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { ProgressIndicator } from 'shared/components/Carousel/ProgressIndicator';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { Stack } from 'shared/components/Stack';
import { SwiperCarousel } from 'shared/components/Carousel/SwiperCarousel';
import { t } from 'localization/utils/t';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useVariantDetailData } from 'productTemplate/hooks/useVariantDetailData';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';

const TP = 'productTemplate.components.buyBar.VariantDetail.Update2022VariantDetail';

export type IUpdate2022VariantDetailProps = IVariantDetailProps;

export const Update2022VariantDetail = ({
  className,
  variantData,
}: IUpdate2022VariantDetailProps) => {
  const {
    carousel: {
      carouselImages,
      nextClassName,
      previousClassName,
      progressIndicatorCurrent,
      progressIndicatorTotal,
      handleSlideChange,
    },
    slug,
    size,
    shoeCondition,
    boxCondition,
    variantId,
    year,
    name,
    price,
    canReturn,
    productTemplateId,
    details,
    termsCopy,
    globalPricing,
    checkoutLink,
    handleCheckoutClick,
  } = useVariantDetailData(variantData);
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const { isOfferable, showPlaceOfferModal } = useOffersContext();
  const [isLoading, setIsLoading] = useState(true);
  const [existingOffer, setExistingOffer] = useState<IUserProductIdOfferResponse>(null);
  const { selectedCurrencyCode } = useCurrency();
  const isMounted = useIsMounted();
  const { isMobileOrTablet } = useWindowSize();
  const isCheckoutRolloutEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_CHECKOUT_PLATFORM_ROLLOUT,
  );

  // TODO: since we are making a call here to update the variant in the provider
  // already, we should not call it again when we show the place offer modal
  // to avoid regression, 2 of the same call is made for now
  // but we should pass only the props needed to placemodaloffer without having it rely on the provider

  async function fetchExistingOffers() {
    try {
      const existingProductIdOfferData: IUserProductIdOfferResponse = await requestService({
        url: `/web-api/v1/wants/find_offer?productId=${variantId}`,
        method: 'get',
      });
      if (isMounted() && !isEmpty(existingProductIdOfferData)) {
        setExistingOffer(existingProductIdOfferData);
      }
    } catch (error) {
      bugsnag(error, { context: 'Fetching existing offers' });
    }
  }

  useEffect(() => {
    // fetch offer first before displaying the content
    if (isMounted()) {
      if (isAuthenticated) {
        void fetchExistingOffers().finally(() => {
          // check again after call
          if (isMounted()) {
            setIsLoading(false);
          }
        });
      } else {
        setIsLoading(false);
      }
    }
    return () => setIsLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOfferClick = () => {
    const options = {
      onDeletedOfferCallbackFn: () => setExistingOffer(null),
    };
    showPlaceOfferModal(variantData?.size, variantData, existingOffer, options);

    if (existingOffer?.offerAmountCents) {
      sendTrackingEvent('OFFER_EDIT_TAP', {
        product_template_slug: slug,
        product_id: productTemplateId,
        size,
        condition_category: shoeCondition,
        shoe_condition: shoeCondition,
        box_condition: boxCondition,
        wantId: existingOffer?.id,
        offer_duration: existingOffer?.offerDuration,
        offer_amount: existingOffer?.offerAmountCents / 100,
        last_sale: null,
        top_offer: null,
        buy_now: variantData?.localizedPriceCents?.amount / 100,
      });
    } else {
      sendTrackingEvent('OFFER_TAP', {
        product_template_slug: slug,
        product_id: productTemplateId,
        size,
        condition_category: shoeCondition,
        shoe_condition: shoeCondition,
        box_condition: boxCondition,
        last_sale: null,
        top_offer: null,
        buy_now: variantData?.localizedPriceCents?.amount / 100,
        currency_code: selectedCurrencyCode,
        event_context: isMobileOrTablet ? 'mobile_web_buy_bar' : 'desktop_web_buy_bar',
      });
    }
  };

  const redirectToCheckout = (event) => {
    event.preventDefault();

    if (isCheckoutRolloutEnabled) {
      window.location.href = checkoutLink;
    } else {
      void router.push(checkoutLink);
    }
  };

  return (
    <Stack className={className} direction="column" spacing="24px">
      <Update2022VariantDetail.CarouselWrapper>
        <Update2022VariantDetail.SwiperCarouselWrapper>
          <SwiperCarousel
            itemList={carouselImages}
            carouselItem={<GalleryItemVariant variantId={variantId} />}
            // swiper props below
            onSlideChange={handleSlideChange}
            centeredSlides
            slidesPerView={1}
            navigation={{
              prevEl: `.${previousClassName}`,
              nextEl: `.${nextClassName}`,
            }}
          />
        </Update2022VariantDetail.SwiperCarouselWrapper>
        {carouselImages.length > 1 && (
          <>
            <Update2022VariantDetail.PreviousButton
              className={previousClassName}
              direction="left"
              showOnHover
            />
            <Update2022VariantDetail.NextButton
              className={nextClassName}
              direction="right"
              showOnHover
            />
          </>
        )}
      </Update2022VariantDetail.CarouselWrapper>

      <Update2022VariantDetail.ProgressIndicator
        activeIndex={progressIndicatorCurrent}
        itemListLength={progressIndicatorTotal}
      />

      {(Boolean(year) || Boolean(name) || Boolean(price)) && (
        <Update2022VariantDetail.NameRowStack direction="row" spacing="8px">
          {Boolean(year) && <span>{year}</span>}
          {Boolean(name) && <Update2022VariantDetail.Name>{name}</Update2022VariantDetail.Name>}
          {Boolean(price) && <LocalizedCurrency price={price} />}
        </Update2022VariantDetail.NameRowStack>
      )}

      <Stack direction="column" spacing="8px">
        {details.map(({ label, value }) => {
          if (label === 'listingDescription') {
            return (
              <Update2022VariantDetail.DetailsRow
                key={label}
                data-qa="details_row"
                direction="row"
                className={
                  isMobileOrTablet
                    ? 'tw-bg-[#1A1A1A] tw-text-white !tw-mt-5'
                    : 'tw-bg-[#F4F4F4] tw-text-black !tw-mt-5'
                }
              >
                <div className="tw-px-3 tw-py-2">
                  <b>{t(`${TP}.sellerNotes`, 'Seller Notes')}</b>: {value}
                </div>
              </Update2022VariantDetail.DetailsRow>
            );
          }
          return (
            <Update2022VariantDetail.DetailsRow key={label} data-qa="details_row" direction="row">
              <span>{label}</span>
              <Update2022VariantDetail.DetailsRowValue>
                {localizeCondition(value)}
              </Update2022VariantDetail.DetailsRowValue>
            </Update2022VariantDetail.DetailsRow>
          );
        })}
      </Stack>

      <Update2022VariantDetail.LowerDetailsStack direction="column" spacing="8px">
        {!canReturn && <>{termsCopy}</>}
        {globalPricing}
      </Update2022VariantDetail.LowerDetailsStack>

      <Stack direction="row" spacing="8px">
        <Update2022VariantDetail.CheckoutLink role="button" onClick={redirectToCheckout}>
          <Update2022VariantDetail.CTAButton
            inverted
            data-qa="select_button"
            onClick={handleCheckoutClick}
          >
            {t(`${TP}.buy`, 'Buy')}
          </Update2022VariantDetail.CTAButton>
        </Update2022VariantDetail.CheckoutLink>

        {isOfferable && (
          <Update2022VariantDetail.CTAButton
            inverted
            data-qa="variant_window_offer_button"
            onClick={handleOfferClick}
            loading={isLoading}
          >
            {existingOffer ? t(`${TP}.updateOffer`, 'Update Offer') : t(`${TP}.offer`, 'Offer')}
          </Update2022VariantDetail.CTAButton>
        )}
      </Stack>
    </Stack>
  );
};

Update2022VariantDetail.CarouselWrapper = styled.div`
  display: grid;
  grid-template-columns: [start] auto [left-end] 1fr [right-start] auto [end];
  grid-template-rows: [top] auto [bottom];
`;

Update2022VariantDetail.SwiperCarouselWrapper = styled.div`
  overflow: hidden;
  grid-column: start / end;
  grid-row: top / bottom;
`;

Update2022VariantDetail.ArrowButton = styled(ArrowButton)`
  grid-row: top / bottom;
  height: auto;
`;

Update2022VariantDetail.PreviousButton = styled(Update2022VariantDetail.ArrowButton)`
  grid-column: start / left-end;
`;

Update2022VariantDetail.NextButton = styled(Update2022VariantDetail.ArrowButton)`
  grid-column: right-start / end;
`;

Update2022VariantDetail.ProgressIndicator = styled(ProgressIndicator)`
  text-align: center;
  ${variableFontRegular({ height: '9.5px', size: '9.5px' })}
  margin-top: 16px;
`;

Update2022VariantDetail.PlainTextStack = styled(Stack)`
  ${variableFontRegular({ height: '14px', size: '11.5px' })}
`;

Update2022VariantDetail.NameRowStack = styled(Update2022VariantDetail.PlainTextStack)`
  margin-top: 24px;
  margin-bottom: 8px;
`;

Update2022VariantDetail.Name = styled.span`
  flex-grow: 1;
`;

Update2022VariantDetail.DetailsRow = styled(Update2022VariantDetail.PlainTextStack)`
  justify-content: space-between;
  text-transform: capitalize;

  html[lang='fr-fr'] & {
    text-transform: none;
  }
`;

Update2022VariantDetail.DetailsRowValue = styled.span`
  max-width: 60%;
  text-align: right;
`;

Update2022VariantDetail.LowerDetailsStack = styled(Update2022VariantDetail.PlainTextStack)`
  text-align: left;
  margin-top: 32px;
`;

Update2022VariantDetail.CTAButton = styled(Button)`
  background: none;
  color: currentcolor;
  border-color: currentcolor;
  margin-top: 10px;
`;

Update2022VariantDetail.CheckoutLink = styled.a`
  width: 100%;
`;
